import React from 'react'
import styled from 'styled-components'
import { UserJobOfferMasterSkillsInner, UserJobOfferAppealsInner } from 'javascripts/api/generated'
import '../../../../../javascript/i18n'
import DefaultFavoriteImage from 'images/job_offers/favorite_job_offers/btn_favorite-default.svg'
import OnFavoriteImage from 'images/job_offers/favorite_job_offers/btn_favorite-on.svg'
import ApplicationHistoryIcon from 'images/job_offers/application_histories/icon_paper_airplane_hover.svg'
import { useFormatNumber } from 'spa/hooks/useFormatNumber'
import { BorderDirection, StyledJobOfferCardProps } from './types'

const StyledCard = styled.div<{ borderDirection?: BorderDirection }>`
  display: flex;
  flex-direction: column;
  padding: 16px;
  width: 100%;
  position: relative;

  background: ${props => props.theme.members.colors.white};
  box-shadow: 0 4px 12px 0 rgba(0, 0, 0, .04);
  border-style: solid;
  border-width: 1px 0;
  border-color: ${props => props.theme.members.colors.lightGray};

  @media screen and ${props => props.theme.media.sm} {
    padding: 24px;
    border-width: 1px;

    ${props => {
      return String(props.borderDirection) === 'horizontal' && `
        border-width: 1px 0;
      `
    }
  }
`

const JobTitle = styled.h2`
  display: inline-flex;
  gap: 8px;
  align-items: flex-start;
  border-bottom: 1px solid ${props => props.theme.members.colors.lightGray};
  padding-bottom: 16px;
  font-weight: 700;
`

const StyledLabel = styled.span`
  display: inline-flex;
  align-items: flex-start;
  gap: 8px;
  padding: 8px;

  border: 1px solid ${props => props.theme.members.colors.silver._100};
  border-radius: 4px;
  background: ${props => props.theme.members.colors.white};

  color: ${props => props.theme.members.colors.silver._100};
  font-size: .6875rem;
  font-style: normal;
  font-weight: 700;
  text-align: center;
  line-height: 100%;

  white-space: nowrap;
  height: fit-content;

  @media ${props => props.theme.media.sm} {
    font-size: 0.75rem;
    gap: 4px;
  }
`

const StyledNewLabel = styled(StyledLabel)`
  background: #FF7374;
  color: ${props => props.theme.members.colors.white};
  border: none;
`

const StyledCloseLabel = styled(StyledLabel)`
  background: ${props => props.theme.members.colors.silver._50};
  color: ${props => props.theme.members.colors.white};
  border: none;
`

const StyledAppliedLabel = styled(StyledCloseLabel)`
  line-height: 120%;

  &::before {
    content: none;

    @media ${props => props.theme.media.sm} {
      content: '';
      display: inline-block;
      width: 0.875rem;
      height: 0.875rem;
      background: url(${ApplicationHistoryIcon}) no-repeat 0 0;
      background-size: 0.875rem;
      vertical-align: text-top;
    }
  }
`

const StyledTitle = styled.a`
  font-size: 1rem;
  font-weight: 700;
  line-height: 170%;
  text-decoration-line: underline;
  text-decoration-thickness: 1px;
  color: ${props => props.theme.members.colors.appleGreen._90};

  &:hover {
    color: ${props => props.theme.members.colors.appleGreen._90};
    text-decoration: none;
    cursor: pointer;
  }

  @media ${props => props.theme.media.sm} {
    font-size: 1.25rem;
  }
`

const JobPrice = styled.div`
  line-height: 170%;
`

const StyledPrice = styled.span`
  font-size: 1.125rem;
  font-weight: 700;
  color: ${props => props.theme.members.colors.orange._50};

  @media ${props => props.theme.media.sm} {
    font-size: 1.5rem;
  }
`

const StyledText = styled.span`
  font-size: 0.6875rem;
  font-weight: 400;

  @media ${props => props.theme.media.sm} {
    font-size: 0.875rem;
  }
`

const ItemList = styled.div`
  display: flex;
  flex-direction: row;
  flex-flow: row wrap;
  column-gap: 16px;
  font-size: 0.75rem;
  margin: 8px 0;

  @media ${props => props.theme.media.sm} {
    margin-top: 0px;
    column-gap: 24px;
    font-size: 0.875rem;
  }
`

const LabelList = styled(ItemList)`
  margin: 0;
  gap: 4px;
  max-width: calc(100% - 89px);

  @media ${props => props.theme.media.sm} {
    padding-top: 18px;
    gap: 8px;
    max-width: calc(100% - 104px);
  }
`

const CardFooter = styled(ItemList)`
  margin-top: 16px;
  gap: 25px;
  justify-content: space-between;
  min-height: 4rem;
  margin-bottom: 0;
  position: relative;

  @media ${props => props.theme.media.sm} {
    margin-top: 0;
  }
`

const StyledList = styled.dl`
  display: flex;
  align-items: flex-start;
  align-self: stretch;
  margin: 0;
`

const StyledTerm = styled.dt`
  font-weight: 700;
  line-height: 200%;
`

const StyledDescription = styled.dd`
  font-weight: 400;
  line-height: 200%;
  margin: 0;
`

const JobDescription = styled.div`
  font-size: 0.875rem;
  font-weight: 400;
  line-height: 170%;
  margin: 8px 0;
  overflow: hidden;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 4;

  @media ${props => props.theme.media.sm} {
    font-size: 1rem;
    -webkit-line-clamp: 2;
  }
`

const FavoriteButton = styled.a`
  position: absolute;
  right: 1rem;
  bottom: 1rem;x

  &::before {
    content: '';
    position: relative;
    display: block;
    width: 4rem;
    height: 4rem;
    background-image: url(${DefaultFavoriteImage});
    background-size: cover;
  }

  &:hover {
    cursor: pointer;
  }

  &.active::before {
    background-image: url(${OnFavoriteImage});
  }

  @media ${props => props.theme.media.sm} {
    right: 1.5rem;
    bottom: 1.5rem;
  }
}
`

const JobStatusLabel = (isOpen: boolean, isNew: boolean) => {
  if (!isOpen) {
    return <StyledCloseLabel>募集終了</StyledCloseLabel>
  } else if (isNew) {
    return <StyledNewLabel>NEW</StyledNewLabel>
  }
}

const { formatNumber } = useFormatNumber()

const masterSkillNames = (skills: UserJobOfferMasterSkillsInner[]): string => {
  return skills?.length ? skills.map((skill) => skill.name).join(', ') : ''
}

const displayRequiredWorkingDays = (days: number): string => {
  return days ? `週${days}日` : '-'
}

const StyledDefinition = (term: string, description: string) => {
  return (
    <StyledList>
      <StyledTerm>{term}</StyledTerm>
      <StyledDescription>{description}</StyledDescription>
    </StyledList>
  )
}

const AppealLabels = (appeals: UserJobOfferAppealsInner[]) => {
  return appeals?.map((appeal) => {
    return <StyledLabel key={appeal.id} data-cy="job-offer-card-appeal">{appeal.description}</StyledLabel>
  })
}

export const JobOfferCard: React.FC<StyledJobOfferCardProps> = props => {
  return (
    <StyledCard borderDirection={props.borderDirection} data-cy='job-offer-card'>
      <JobTitle>
        {JobStatusLabel(props.jobOffer.open, props.jobOffer.new)}
        <StyledTitle href={`/job_offers/${props.jobOffer.id}`} data-cy="job-offer-card-title">{props.jobOffer.title}</StyledTitle>
      </JobTitle>
      <JobPrice>
        <StyledText>〜 </StyledText>
        <StyledPrice data-cy="job-offer-card-max-unit-price">{formatNumber(props.jobOffer.maxUnitPrice)}</StyledPrice>
        <StyledText> 円／月</StyledText>
        <StyledText>（※月160時間稼働の場合・税別）</StyledText>
      </JobPrice>
      <ItemList data-cy="job-offer-card-detail-information">
        {StyledDefinition('職種：', props.jobOffer.occupation?.name)}
        {StyledDefinition('スキル：', masterSkillNames(props.jobOffer.masterSkills))}
        {StyledDefinition('エリア：', props.jobOffer.officeLocation)}
        {StyledDefinition('最低稼働日数：', displayRequiredWorkingDays(props.jobOffer.requiredWorkingDays))}
      </ItemList>
      <JobDescription>{props.jobOffer.description}</JobDescription>
      <CardFooter data-cy="job-offer-card-footer-information">
        <LabelList>
          {props.isApplied && <StyledAppliedLabel>応募済み</StyledAppliedLabel>}
          {AppealLabels(props.jobOffer.appeals)}
        </LabelList>
      </CardFooter>
      <FavoriteButton data-cy="job-offer-favorite-btn" className={props.isFavorite ? 'active' : ''} onClick={props.onClick} />
    </StyledCard>
  )
}
